import dayjs from 'dayjs'
/**
 * Calculate the number of days between two dates.
 * @param {Date | string} startDate - The start date.
 * @param {Date | string} endDate - The end date.
 * @returns {number} The number of days between the start and end dates.
 */
export function calculateDaysBetweenDates(startDate: Date | string, endDate: Date | string): number {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate

  const startMs = start.getTime()
  const endMs = end.getTime()

  const differenceMs = Math.abs(endMs - startMs)

  return Math.round(differenceMs / (1000 * 60 * 60 * 24))
}

/**
 * Compare two dates and return a string representing the difference in years, months, days, and hours.
 * @param {Date | string} startDate - The start date.
 * @param {Date | string} endDate - The end date.
 * @returns {string} A string representing the difference between the start and end dates in years, months, days, and hours.
 */
export function compareDates(startDate: Date | string, endDate: Date | string): string {
  let duration = dayjs.duration(0)
  if (endDate) {
    duration = dayjs.duration(dayjs(endDate).diff(startDate))
  } else duration = dayjs.duration(dayjs().diff(startDate))
  if (duration.asYears() >= 1) {
    return duration.format('Y[a] M[m]')
  } else if (duration.asMonths() >= 1) {
    return duration.format('M[m] D[j]')
  } else if (duration.asDays() >= 1) {
    return duration.format('D[j] HH[h]')
  } else if (duration.asHours() >= 1) {
    return duration.format('H[h] mm[m]')
  } else if (duration.asMinutes() >= 1) {
    return duration.format('m[m] ss[s]')
  }

  return duration.format('s[s]')
}

/**
 * Compare two dates and return a string representing the difference days.
 * @param {Date | string} startDate - The start date.
 * @param {Date | string} endDate - The end date.
 * @returns {string} A string representing the difference between the start and end dates in days.
 */
export function compareDays(startDate: Date | string, endDate: Date | string): string {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate

  const differenceMs = Math.abs(end.getTime() - start.getTime())

  const days = Math.round(differenceMs / (1000 * 60 * 60 * 24))

  return days === 0 ? `J1` : `J${days}`
}

/**
 * Calculate the particular day in milliseconds based on the provided day, operation and month.
 * @param {number} day - particular day to add or subtract.
 * @param {string} operation - add or subtract operation to be performed
 * @param {number} month - particular month to add or subtract.
 * @returns {string} A string representing the calculated day in milliseconds .
 */
export const daysCalculator = (day: number, operation: string, month?: number) => {
  let date = new Date()
  let calculatedDay: any
  if (month) {
    date.setMonth(date.getMonth() - month)
  }
  switch (operation) {
    case 'add':
      calculatedDay = new Date(date.getTime() + day * 24 * 60 * 60 * 1000)
      break
    case 'subtract':
      calculatedDay = new Date(date.getTime() - day * 24 * 60 * 60 * 1000)
      break
  }
  // Set to midnight
  calculatedDay.setHours(0, 0, 0, 0)
  return calculatedDay.getTime()
}

/**
 * Adds a specified amount of time (days, weeks, months, or years) to a given date.
 * @param {Date} date - The starting date to which time will be added.
 * @param {Object} addition - An object containing the type of time unit (days, weeks, months, or years) and the value to add.
 * @param {string} addition.type - The type of time unit to add ("days", "weeks", "months", or "years").
 * @param {number} addition.value - The number of units to add (positive for addition, negative for subtraction).
 * @returns {Date} A new Date object representing the adjusted date.
 */
export const addToDate = (date: Date, addition: any) => {
  const newDate = new Date(date)

  switch (addition.type) {
    case 'days':
      newDate.setDate(newDate.getDate() + addition.value)
      break
    case 'weeks':
      newDate.setDate(newDate.getDate() + addition.value * 7)
      break
    case 'months':
      newDate.setMonth(newDate.getMonth() + addition.value)
      break
    case 'years':
      newDate.setFullYear(newDate.getFullYear() + addition.value)
      break
    default:
      throw new Error('Unsupported type: ' + addition.type)
  }

  return newDate
}

export const isDateInThisWeek = (referenceDateString: any) => {
  const currentDate = new Date()
  const referenceDate = new Date(referenceDateString)

  // Get the start and end of the current week
  const startOfWeek = new Date(currentDate)
  startOfWeek.setDate(currentDate.getDate() - currentDate.getDay())

  const endOfWeek = new Date(startOfWeek)
  endOfWeek.setDate(startOfWeek.getDate() + 7)

  // Check if the reference date is within this week
  return referenceDate >= startOfWeek && referenceDate <= endOfWeek
}

export const getRelativeDateType = (referenceDate: string) => {
  const currentDate = new Date()
  const refDate = new Date(referenceDate)

  // Calculate the difference in milliseconds
  const diffInMs = refDate.getTime() - currentDate.getTime()
  const absDiffInMs = Math.abs(diffInMs)

  // Calculate detailed time differences
  const totalSeconds = Math.floor(absDiffInMs / 1000)
  const totalMinutes = Math.floor(totalSeconds / 60)
  const totalHours = Math.floor(totalMinutes / 60)
  const totalDays = Math.floor(totalHours / 24)
  const totalMonths = Math.floor(totalDays / 30)
  const totalYears = Math.floor(totalDays / 365)

  // Calculate remaining time for detailed breakdown
  const remainingSeconds = totalSeconds % 60
  const remainingMinutes = totalMinutes % 60
  const remainingHours = totalHours % 24
  const remainingDays = totalDays % 30
  const remainingMonths = totalMonths % 12

  if (totalDays === 0) {
    return {
      type: 'today',
      format: 'day',
      originalDate: referenceDate,
      detail: {
        hours: totalHours,
        minutes: remainingMinutes,
        sec: remainingSeconds,
      },
    }
  } else if (diffInMs > 0) {
    // Future dates
    if (totalYears >= 1) {
      return {
        type: 'after',
        value: totalYears,
        format: 'year',
        detail: {
          year: totalYears,
          month: remainingMonths,
          day: remainingDays,
          hours: remainingHours,
          minutes: remainingMinutes,
          sec: remainingSeconds,
        },
      }
    } else if (totalMonths >= 1) {
      return {
        type: 'after',
        value: totalMonths,
        format: 'month',
        detail: {
          month: totalMonths,
          day: remainingDays,
          hours: remainingHours,
          minutes: remainingMinutes,
          sec: remainingSeconds,
        },
      }
    } else {
      return {
        type: 'after',
        value: totalDays,
        format: 'day',
        comingWeek: isDateInThisWeek(refDate),
        detail: {
          day: totalDays,
          hours: remainingHours,
          minutes: remainingMinutes,
          sec: remainingSeconds,
        },
      }
    }
  } else {
    // Past dates (before current date)
    if (totalYears >= 1) {
      return {
        type: 'before',
        value: totalYears,
        format: 'year',
        detail: {
          year: totalYears,
          month: remainingMonths,
          day: remainingDays,
          hours: remainingHours,
          minutes: remainingMinutes,
          sec: remainingSeconds,
        },
      }
    } else if (totalMonths >= 1) {
      return {
        type: 'before',
        value: totalMonths,
        format: 'month',
        detail: {
          month: totalMonths,
          day: remainingDays,
          hours: remainingHours,
          minutes: remainingMinutes,
          sec: remainingSeconds,
        },
      }
    } else {
      return {
        type: 'before',
        value: totalDays,
        format: 'day',
        originalDate: referenceDate,
        detail: {
          day: totalDays,
          hours: remainingHours,
          minutes: remainingMinutes,
          sec: remainingSeconds,
        },
      }
    }
  }
}

export const minutesFromMidnight = (time: { hours: number; minutes: number }) => {
  const { hours, minutes } = time
  return hours * 60 + minutes
}

export const timeFromMinutes = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return { hours, minutes }
}

export const dueDateDisplay = (data: any, display = true) => {
  if (!display) return ''

  if (!data || !data.detail) return ''

  const { format, type, detail } = data

  switch (format) {
    case 'year':
      // Show: 1a 2m (1 year 2 months)
      const yearText = detail.year > 1 ? `${detail.year}a` : `${detail.year}a`
      const monthText = detail.month > 0 ? ` ${detail.month}m` : ''
      return `${yearText}${monthText}`

    case 'month':
      // Show: 2m 5j (2 months 5 days)
      const monthOnlyText = detail.month > 1 ? `${detail.month}m` : `${detail.month}m`
      const dayText = detail.day > 0 ? ` ${detail.day}j` : ''
      return `${monthOnlyText}${dayText}`

    case 'day':
      if (type === 'before') {
        // Overdue tasks: 5j 3h (5 days 3 hours)
        const dayBeforeText = detail.day > 0 ? `${detail.day}j` : ''
        const hourText = detail.hours > 0 ? ` ${detail.hours}h` : ''
        return `${dayBeforeText}${hourText}`.trim()
      } else if (type === 'today') {
        // Today tasks: 12h 3min (12 hours 3 minutes) or 3min 5s (3 minutes 5 seconds)
        if (detail.hours > 0) {
          const todayHourText = `${detail.hours}h`
          const todayMinText = detail.minutes > 0 ? ` ${detail.minutes}min` : ''
          return `${todayHourText}${todayMinText}`
        } else if (detail.minutes > 0) {
          const todayMinOnlyText = `${detail.minutes}min`
          const secText = detail.sec > 0 ? ` ${detail.sec}s` : ''
          return `${todayMinOnlyText}${secText}`
        } else {
          return `${detail.sec}s`
        }
      } else {
        // Future tasks: regular day format
        return detail.day > 1 ? `${detail.day}j` : `${detail.day}j`
      }

    default:
      return ''
  }
}
